import type {
  MyPermissions,
  PermissionCode,
  PermissionScope,
} from "@/types/authorization";
import { MANAGE_ROLES } from "@/types/authorization";

/** Widest-last, so "at this scope or wider" is an index comparison. */
const SCOPE_ORDER: PermissionScope[] = ["TEAM", "ALL"];

/**
 * Whether the capability set holds `code`, optionally at `scope` or wider.
 *
 * Not holding a permission is **not** an error state — it means "your own
 * records only", which is what every employee gets for free. Use `can()` to
 * decide whether to show a screen about *other people*, never to decide whether
 * someone may see their own.
 */
export function can(
  capabilities: MyPermissions | undefined | null,
  code: PermissionCode,
  scope?: PermissionScope
): boolean {
  const held = capabilities?.permissions.find((p) => p.code === code)?.scope;
  if (!held) return false;
  if (!scope) return true;
  return SCOPE_ORDER.indexOf(held) >= SCOPE_ORDER.indexOf(scope);
}

/** Whether any capability in a module is held — gates a whole nav section. */
export function canAnyIn(
  capabilities: MyPermissions | undefined | null,
  module: string
): boolean {
  return (capabilities?.permissions ?? []).some((p) =>
    p.code.startsWith(`${module}.`)
  );
}

/** Whether any of `codes` is held. */
export function canAny(
  capabilities: MyPermissions | undefined | null,
  codes: PermissionCode[]
): boolean {
  return codes.some((code) => can(capabilities, code));
}

/**
 * Whether the user can manage roles and permissions — a permission check,
 * not a flag. Gates the RBAC screen and anything as sensitive (setting
 * someone's app role, editing a locked SKU); it is **not** the same
 * question as "is this role the Admin role" — a role can hold
 * `MANAGE_ROLES` alone without holding everything else, and reads as such
 * (see `Role.is_admin_role`, which this deliberately does not check).
 */
export function isAdmin(capabilities: MyPermissions | undefined | null): boolean {
  return can(capabilities, MANAGE_ROLES);
}

/** The seeded employee role — the one role that stays in the `/user` shell. */
const EMPLOYEE_ROLE_NAME = "Normal";

/**
 * Whether the user can reach the admin shell at all. The server decides
 * (`can_reach_admin_shell`: anything held beyond the employee baseline), so a
 * renamed or cloned employee role still lands in `/user` and a role given one
 * admin code reaches `/admin` (T-205). A user holding no permissions (no
 * role, or the permissions failed to load) stays in `/user`.
 *
 * Fallback for a backend that doesn't send the flag yet: the seeded `Normal`
 * role stays in `/user`, every other role reaches `/admin`.
 */
export function hasAnyCapability(
  capabilities: MyPermissions | undefined | null
): boolean {
  if (!capabilities?.permissions.length) return false;
  if (capabilities.can_reach_admin_shell !== undefined) {
    return capabilities.can_reach_admin_shell;
  }
  return capabilities.role?.name !== EMPLOYEE_ROLE_NAME;
}
